import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.swingsetupai.com/api';

const apiClient = axios.create({ baseURL: API_BASE_URL });

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

/* ── Types ─────────────────────────────────────────────────────── */

export interface DashboardSummary {
  totalUsers: number;
  premiumUsers: number;
  freeUsers: number;
  totalRevenue: number;
}

export interface AdminUserDetail {
  id: number;
  name: string;
  email: string;
  premium: boolean;
  plan: string | null;
  startDate: string | null;   // ISO LocalDateTime string
  endDate: string | null;
  amountPaid: number | null;
  joinedAt: string | null;
}

/* ── API calls ──────────────────────────────────────────────────── */

export const fetchDashboardSummary = async (
  from: string,
  to: string
): Promise<DashboardSummary> => {
  const { data } = await apiClient.get('/admin/dashboard/summary', {
    params: { from, to },
  });
  return data.data as DashboardSummary;
};

export const fetchAdminUserList = async (
  from: string,
  to: string
): Promise<AdminUserDetail[]> => {
  const { data } = await apiClient.get('/admin/dashboard/users', {
    params: { from, to },
  });
  return data.data as AdminUserDetail[];
};

/* ── Market Screener Types ──────────────────────────────────────── */

export interface MarketScreenerType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  category: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface MarketScreenerTypeRequest {
  code: string;
  name: string;
  description?: string;
  category: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface MarketScreenerResult {
  id: number;
  screenerId: number;
  screenerName: string;
  symbol: string;
  companyName: string | null;
  exchange: string;
  ltp: number | null;
  changePct: number | null;
  volume: number | null;
  week52High: number | null;
  week52Low: number | null;
  marketCap: number | null;
  marketCapName: string | null;
  sector: string | null;
  industry: string | null;
  rankOrder: number | null;
  asOfDate: string | null;
  createdAt: string;
}

export interface MarketScreenerResultRequest {
  screenerId: number;
  symbol: string;
  companyName?: string;
  exchange?: string;
  ltp?: number;
  changePct?: number;
  volume?: number;
  week52High?: number;
  week52Low?: number;
  marketCap?: number;
  marketCapName?: string;
  sector?: string;
  industry?: string;
  rankOrder?: number;
  asOfDate?: string;
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}


/* ── Chart Pattern Admin ────────────────────────────────────────── */

export interface ChartPatternTypeAdmin {
  id: number;
  code: string;
  name: string;
  description: string | null;
  category: 'REVERSAL' | 'CONTINUATION';
  imageUrl: string | null;
  scanClause: string | null;
  columnClause: string | null;
  columnMapping: string | null;
  chartinkScreenerId: string | null;
  isActive: boolean;
  sortOrder: number;
}

export interface ChartPatternTypeAdminRequest {
  code?: string;
  name: string;
  description?: string;
  category: string;
  imageUrl?: string;
  scanClause?: string;
  columnClause?: string;
  columnMapping?: string;
  chartinkScreenerId?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export const chartPatternAdminApi = {
  getTypes: () =>
    apiClient.get<{ data: ChartPatternTypeAdmin[] }>('/admin/chart-patterns/types'),

  createType: (req: ChartPatternTypeAdminRequest) =>
    apiClient.post<{ data: ChartPatternTypeAdmin }>('/admin/chart-patterns/types', req),

  updateType: (id: number, req: ChartPatternTypeAdminRequest) =>
    apiClient.put<{ data: ChartPatternTypeAdmin }>(`/admin/chart-patterns/types/${id}`, req),

  deleteType: (id: number) =>
    apiClient.delete(`/admin/chart-patterns/types/${id}`),

  toggleActive: (id: number) =>
    apiClient.patch<{ data: ChartPatternTypeAdmin }>(`/admin/chart-patterns/types/${id}/toggle`),

  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<{ data: string }>('/admin/chart-patterns/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

/* ── Chart Tool Plan Admin ──────────────────────────────────────── */

export interface PlanResponse {
  id: number;
  planName: string;
  durationMonths: number;
  price: number;
  originalPrice: number;
  currency: string;
  discountPercent: number;
  description: string | null;
  features: string[] | null;
  isActive: boolean;
  product: string;
}

export interface PlanRequest {
  planName: string;
  durationMonths: number;
  price: number;
  originalPrice: number;
  discountPercent?: number;
  description?: string;
  featuresDescription?: string;
  isActive?: boolean;
}

export const chartToolPlanApi = {
  getAll: () =>
    apiClient.get<{ data: PlanResponse[] }>('/admin/chart-tool/plans'),

  create: (req: PlanRequest) =>
    apiClient.post<{ data: PlanResponse }>('/admin/chart-tool/plans', req),

  update: (id: number, req: PlanRequest) =>
    apiClient.put<{ data: PlanResponse }>(`/admin/chart-tool/plans/${id}`, req),

  deactivate: (id: number) =>
    apiClient.delete(`/admin/chart-tool/plans/${id}`),

  reactivate: (id: number) =>
    apiClient.patch(`/admin/chart-tool/plans/${id}/reactivate`),
};

export const marketScreenerApi = {
  getTypes: () =>
    apiClient.get<{ data: MarketScreenerType[] }>('/admin/market-screener/types'),

  createType: (req: MarketScreenerTypeRequest) =>
    apiClient.post<{ data: MarketScreenerType }>('/admin/market-screener/types', req),

  updateType: (id: number, req: MarketScreenerTypeRequest) =>
    apiClient.put<{ data: MarketScreenerType }>(`/admin/market-screener/types/${id}`, req),

  deleteType: (id: number) =>
    apiClient.delete(`/admin/market-screener/types/${id}`),

  getResults: (screenerId: number, page = 1, size = 20) =>
    apiClient.get<{ data: PagedResponse<MarketScreenerResult> }>(
      `/admin/market-screener/results/${screenerId}`,
      { params: { page, size } }
    ),

  addResult: (req: MarketScreenerResultRequest) =>
    apiClient.post<{ data: MarketScreenerResult }>('/admin/market-screener/results', req),

  deleteResult: (id: number) =>
    apiClient.delete(`/admin/market-screener/results/${id}`),

  clearResults: (screenerId: number) =>
    apiClient.delete(`/admin/market-screener/results/clear/${screenerId}`),
};
